import { useState, useEffect, useCallback } from "react";
import {
  CPTSlab,
  calculateSupplierCPTSlab,
  DEFAULT_HLD_DISTANCE_METRES,
} from "../utils/cptSlabUtils";
import { SelectedSupplier } from "../utils/partsCalculationUtils";

interface UseSupplierCPTSlabsParams {
  selectedSuppliers: SelectedSupplier[];
  orderData: any;
  hldDistanceMetres?: number;
}

interface UseSupplierCPTSlabsReturn {
  supplierCPTSlabs: Map<string, CPTSlab>;
  getSuggestedCPTSlab: (supplier: SelectedSupplier) => CPTSlab;
  handleCPTSlabChange: (supplierId: string, newSlab: CPTSlab) => void;
  getSuppliersWithCPTSlab: () => SelectedSupplier[];
}

export const useSupplierCPTSlabs = ({
  selectedSuppliers,
  orderData,
  hldDistanceMetres = DEFAULT_HLD_DISTANCE_METRES,
}: UseSupplierCPTSlabsParams): UseSupplierCPTSlabsReturn => {
  const [supplierCPTSlabs, setSupplierCPTSlabs] = useState<
    Map<string, CPTSlab>
  >(new Map());

  useEffect(() => {
    const newCPTSlabs = new Map<string, CPTSlab>();

    selectedSuppliers.forEach((supplier) => {
      const existingCPTSlab = supplierCPTSlabs.get(supplier.supplierId);

      if (existingCPTSlab !== undefined) {
        newCPTSlabs.set(supplier.supplierId, existingCPTSlab);
      } else {
        const suggestedSlab = calculateSupplierCPTSlab(
          orderData,
          supplier.distanceKm,
          supplier.supplierCity,
          hldDistanceMetres
        );
        newCPTSlabs.set(supplier.supplierId, suggestedSlab);
      }
    });

    setSupplierCPTSlabs(newCPTSlabs);
  }, [selectedSuppliers, orderData, hldDistanceMetres]);

  const getSuggestedCPTSlab = useCallback(
    (supplier: SelectedSupplier): CPTSlab => {
      return calculateSupplierCPTSlab(
        orderData,
        supplier.distanceKm,
        supplier.supplierCity,
        hldDistanceMetres
      );
    },
    [orderData, hldDistanceMetres]
  );

  const handleCPTSlabChange = useCallback(
    (supplierId: string, newSlab: CPTSlab) => {
      setSupplierCPTSlabs((prev) => {
        const updated = new Map(prev);
        updated.set(supplierId, newSlab);
        return updated;
      });
    },
    []
  );

  const getSuppliersWithCPTSlab = useCallback((): SelectedSupplier[] => {
    return selectedSuppliers.map((supplier) => ({
      ...supplier,
      cptSlab: supplierCPTSlabs.get(supplier.supplierId),
    }));
  }, [selectedSuppliers, supplierCPTSlabs]);

  return {
    supplierCPTSlabs,
    getSuggestedCPTSlab,
    handleCPTSlabChange,
    getSuppliersWithCPTSlab,
  };
};
